import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { type SearchMiddleware, useSearch } from '@tanstack/react-router'
import type { FeatureCollection, Geometry } from 'geojson'
import { useMemo, useSyncExternalStore } from 'react'

import { ApiError, apiFetch } from '@/lib/api'
import { type CamadaNaUrl, escreverCamadaNaUrl, lerCamadaDaUrl } from '@/lib/camadaNaUrl'
import { camadasDaGestaoQuery } from '@/lib/camadasGestao'
import { escreverRecorte, lerRecorte, type RecorteNaUrl, validarRecorte } from '@/lib/recorte'
import {
  escreverModoDoTooltip,
  lerModoDoTooltip,
  type ModoNoObservatorioNaUrl,
} from '@/lib/tooltipEmTeste'

/*
 * A camada em teste e a prévia do arquivo: o observatório de verdade, aberto
 * pela Área do Gestor com o desenho de uma camada que o público ainda não vê.
 *
 * - `?camadaEmTeste={id}`: a camada que aguarda o administrador, com o nome e
 *   a cor que o editor escolheu; o administrador olha o desenho e conclui o
 *   teste, o passo que libera a publicação.
 *   A mesma URL abre o desenho guardado de uma camada desativada, para a
 *   gestão revê-lo: sem teste a concluir e sem gravar nada.
 * - `?previaArquivo={id}&cor=%23RRGGBB`: o arquivo que acabou de ser aceito,
 *   ainda sem ir para a fila, na cor que está no formulário. Só olha: não muda
 *   estado nem histórico.
 *
 * O desenho vem da API, com a sessão da gestão, e nunca do servidor de mapas,
 * que é público. Sem sessão ou sem permissão, a API recusa e nada é desenhado.
 */

export type ModoDeTeste =
  { tipo: 'teste'; camada: number } | { tipo: 'previa'; arquivo: number; cor: string }

/** Os parâmetros do modo de teste como ficam na URL. */
export interface ModoDeTesteNaUrl {
  camadaEmTeste?: number
  previaArquivo?: number
  cor?: string
}

const ID = /^[1-9]\d{0,17}$/
const COR = /^#[0-9a-fA-F]{6}$/

function lerId(bruto: unknown): number | undefined {
  if (typeof bruto !== 'number' && typeof bruto !== 'string') return undefined
  const texto = String(bruto)
  if (!ID.test(texto)) return undefined
  const id = Number(texto)
  return Number.isSafeInteger(id) ? id : undefined
}

/**
 * Lê o modo de teste de uma URL e descarta o que não faz sentido, sem quebrar
 * a tela. A camada em teste vence a prévia quando as duas vêm juntas. A prévia
 * sem cor válida não existe: o formulário sempre manda a cor, e desenhar numa
 * cor inventada mostraria ao gestor outra camada que não a dele.
 */
export function lerModoDeTeste(bruto: Record<string, unknown>): ModoDeTeste | undefined {
  const camada = lerId(bruto.camadaEmTeste)
  if (camada !== undefined) return { tipo: 'teste', camada }
  const arquivo = lerId(bruto.previaArquivo)
  const cor = bruto.cor
  if (arquivo !== undefined && typeof cor === 'string' && COR.test(cor)) {
    return { tipo: 'previa', arquivo, cor: cor.toUpperCase() }
  }
  return undefined
}

export function escreverModoDeTeste(modo: ModoDeTeste | undefined): ModoDeTesteNaUrl {
  if (modo === undefined) return {}
  return modo.tipo === 'teste'
    ? { camadaEmTeste: modo.camada }
    : { previaArquivo: modo.arquivo, cor: modo.cor }
}

/**
 * A busca da rota do mapa: o recorte, a camada que abre ligada e, quando há, o
 * modo de teste da camada ou o do tooltip (posicionar ou testar). A camada em
 * teste e o tooltip não vêm juntos: com a camada, o tooltip sai da URL, e a
 * regra da camada não muda.
 */
export type BuscaDoMapa = RecorteNaUrl & CamadaNaUrl & ModoDeTesteNaUrl & ModoNoObservatorioNaUrl

export function validarBuscaDoMapa(bruto: Record<string, unknown>): BuscaDoMapa {
  const modo = lerModoDeTeste(bruto)
  return {
    ...validarRecorte(bruto),
    ...escreverCamadaNaUrl(lerCamadaDaUrl(bruto)),
    ...escreverModoDeTeste(modo),
    ...(modo === undefined ? escreverModoDoTooltip(lerModoDoTooltip(bruto)) : {}),
  }
}

/**
 * Middleware da busca da rota do mapa: o recorte reescrito como sempre, e o
 * modo de teste (da camada ou do tooltip) mantido. Os filtros navegam
 * escrevendo só o recorte; sem isto, o primeiro filtro tirava o gestor do
 * teste no meio dele. O modo do destino vale mais que o da origem.
 *
 * A camada que abre ligada fica só se o destino a traz (o link aberto): ela
 * vale para a abertura, e o primeiro filtro a tira da URL.
 */
export const canonizarBuscaDoMapa: SearchMiddleware<BuscaDoMapa> = ({ search, next }) => {
  const destino = next(search) as Record<string, unknown>
  const origem = search as Record<string, unknown>
  const modo = lerModoDeTeste(destino) ?? lerModoDeTeste(origem)
  const tooltip =
    modo === undefined ? (lerModoDoTooltip(destino) ?? lerModoDoTooltip(origem)) : undefined
  return {
    ...escreverRecorte(lerRecorte(destino)),
    ...escreverCamadaNaUrl(lerCamadaDaUrl(destino)),
    ...escreverModoDeTeste(modo),
    ...escreverModoDoTooltip(tooltip),
  }
}

/** O modo de teste da URL do mapa, ou `undefined` no observatório comum. */
export function useModoDeTeste(): ModoDeTeste | undefined {
  const busca: Record<string, unknown> = useSearch({ strict: false })
  const modo = lerModoDeTeste(busca)
  const tipo = modo?.tipo
  const id = modo === undefined ? undefined : modo.tipo === 'teste' ? modo.camada : modo.arquivo
  const cor = modo?.tipo === 'previa' ? modo.cor : undefined
  // Mesmo modo, mesmo objeto: quem depende dele num efeito não roda à toa.
  return useMemo((): ModoDeTeste | undefined => {
    if (id === undefined) return undefined
    if (tipo === 'teste') return { tipo, camada: id }
    return cor === undefined ? undefined : { tipo: 'previa', arquivo: id, cor }
  }, [tipo, id, cor])
}

// ── O desenho ──────────────────────────────────────────────────────────────

/** O desenho como a API o devolve: `FeatureCollection` GeoJSON em SIRGAS 2000. */
export type Desenho = FeatureCollection

/** A resposta do desenho não tem a forma combinada com a API. */
export class DesenhoInvalido extends Error {
  constructor() {
    super('O desenho da camada não é uma FeatureCollection GeoJSON')
    this.name = 'DesenhoInvalido'
  }
}

export function lerDesenho(corpo: unknown): Desenho {
  if (typeof corpo !== 'object' || corpo === null) throw new DesenhoInvalido()
  const { type, features } = corpo as Record<string, unknown>
  if (type !== 'FeatureCollection' || !Array.isArray(features)) throw new DesenhoInvalido()
  for (const feicao of features as unknown[]) {
    if (typeof feicao !== 'object' || feicao === null) throw new DesenhoInvalido()
    const { type: tipo, geometry } = feicao as Record<string, unknown>
    if (tipo !== 'Feature' || typeof geometry !== 'object' || geometry === null) {
      throw new DesenhoInvalido()
    }
  }
  return corpo as Desenho
}

/**
 * Estende a caixa com cada posição de uma geometria GeoJSON, a qualquer
 * profundidade. Guarda só os quatro extremos, sem juntar as posições numa
 * lista: espalhar a lista em `Math.min(...)` estoura o limite de argumentos de
 * uma chamada quando o desenho aceito tem centenas de milhares de vértices.
 */
function estender(coordenadas: unknown, caixa: [number, number, number, number]): void {
  if (!Array.isArray(coordenadas)) return
  const [x, y] = coordenadas as unknown[]
  if (typeof x === 'number' && typeof y === 'number') {
    if (x < caixa[0]) caixa[0] = x
    if (y < caixa[1]) caixa[1] = y
    if (x > caixa[2]) caixa[2] = x
    if (y > caixa[3]) caixa[3] = y
    return
  }
  for (const filha of coordenadas as unknown[]) estender(filha, caixa)
}

/** `[oeste, sul, leste, norte]` do desenho inteiro, ou `null` se ele não tem posição. */
export function caixaDoDesenho(desenho: Desenho): [number, number, number, number] | null {
  const caixa: [number, number, number, number] = [Infinity, Infinity, -Infinity, -Infinity]
  for (const feicao of desenho.features) {
    const geometria: Geometry | null = feicao.geometry
    if (geometria !== null && 'coordinates' in geometria) estender(geometria.coordinates, caixa)
  }
  return caixa[0] === Infinity ? null : caixa
}

/** O que a resposta da API diz sobre a pessoa e a camada, para a tela escolher a frase. */
export type Recusa =
  'sem-sessao' | 'sem-permissao' | 'senha-pendente' | 'nao-encontrada' | 'nao-aceita' | 'falha'

function codigoDa(erro: ApiError): unknown {
  const { corpo } = erro
  return typeof corpo === 'object' && corpo !== null && 'codigo' in corpo ? corpo.codigo : undefined
}

/**
 * O 403 é lido pelo `codigo`, não só pelo status: o mesmo status vem do cargo
 * sem a área, da senha inicial que ainda não foi trocada e do token CSRF
 * recusado, e só o primeiro quer dizer "só o administrador testa".
 */
export function recusaDe(erro: unknown): Recusa {
  if (!(erro instanceof ApiError)) return 'falha'
  switch (erro.status) {
    case 401:
      return 'sem-sessao'
    case 403: {
      const codigo = codigoDa(erro)
      if (codigo === 'sem-permissao') return 'sem-permissao'
      if (codigo === 'senha-inicial-pendente') return 'senha-pendente'
      return 'falha'
    }
    case 404:
      return 'nao-encontrada'
    case 409:
      return 'nao-aceita'
    default:
      return 'falha'
  }
}

/** A camada da gestão como `GET /v1/admin/camadas` a lista; só o que o teste usa. */
interface CamadaDaGestao {
  id: number
  nome: string
  cor: string
  situacao: string
}

interface ListaDaGestao {
  gestao: CamadaDaGestao[]
  podeTestarEPublicar: boolean
}

/**
 * O arquivo que espera na fila sobre uma camada que já existe. É ele que o
 * teste desenha: na cor dele (a inclusão do arquivo de uma variável traz a
 * cor com que ela vai ao ar) ou na da camada (a troca mantém a cor), e na
 * situação dele, e não na da camada, que segue não entregue ou no ar.
 */
function envioDe(item: Record<string, unknown>): { cor: unknown; situacao: string } | undefined {
  const { envio } = item
  if (typeof envio !== 'object' || envio === null) return undefined
  const { cor, situacao } = envio as Record<string, unknown>
  return { cor, situacao: typeof situacao === 'string' ? situacao : '' }
}

export function lerListaDaGestao(corpo: unknown): ListaDaGestao {
  const objeto = (typeof corpo === 'object' && corpo !== null ? corpo : {}) as Record<
    string,
    unknown
  >
  const gestao = Array.isArray(objeto.gestao) ? (objeto.gestao as unknown[]) : []
  return {
    gestao: gestao.flatMap((item) => {
      if (typeof item !== 'object' || item === null) return []
      const campos = item as Record<string, unknown>
      const envio = envioDe(campos)
      const { id, nome } = campos
      const cor = typeof envio?.cor === 'string' ? envio.cor : campos.cor
      const situacao = envio === undefined ? campos.situacao : envio.situacao
      if (typeof id !== 'number' || typeof nome !== 'string' || typeof cor !== 'string') return []
      if (!COR.test(cor)) return []
      return [
        {
          id,
          nome,
          cor: cor.toUpperCase(),
          situacao: typeof situacao === 'string' ? situacao : '',
        },
      ]
    }),
    podeTestarEPublicar: objeto.podeTestarEPublicar === true,
  }
}

/** O GeoJSON vem como `application/geo+json`; o JSON simples também é aceito. */
const ACEITA_GEOJSON = { Accept: 'application/geo+json, application/json' }

/**
 * O que a faixa oferece: concluir o teste (administrador, camada que aguarda
 * teste), dizer que ele já foi concluído, dizer que só o administrador o
 * conclui (o editor de camadas também vê o desenho) ou nada (a prévia, ou a
 * camada que já saiu da fila).
 */
export type AcaoDoTeste = 'concluir' | 'concluido' | 'so-administrador' | 'nenhuma'

function acaoDoTeste(podeTestar: boolean, situacao: string): AcaoDoTeste {
  if (situacao === 'testada') return 'concluido'
  if (situacao !== 'aguardando-teste') return 'nenhuma'
  return podeTestar ? 'concluir' : 'so-administrador'
}

/** O estado do modo de teste, pronto para a faixa, a lista e o mapa. */
export type CamadaEmTeste =
  | { estado: 'carregando'; modo: ModoDeTeste }
  | { estado: 'recusada'; modo: ModoDeTeste; recusa: Recusa }
  | {
      estado: 'pronta'
      modo: ModoDeTeste
      /** O nome na lista; a prévia não tem nome ainda. */
      nome: string | null
      cor: string
      desenho: Desenho
      acao: AcaoDoTeste
      /**
       * O desenho guardado de uma camada desativada, que a gestão abre para
       * rever ("Ver o desenho no observatório"): só olha, sem teste a concluir.
       */
      desativada?: boolean
    }

/**
 * A lista da gestão que o teste lê vem reduzida (sem o catálogo), então não
 * pode morar na mesma chave da aba Camadas, que lê a resposta inteira: o
 * gestor que voltasse do teste para a aba acharia no cache a lista sem
 * catálogo. A chave fica abaixo da da aba, e invalidar a da aba relê as duas.
 */
export const CHAVE_DA_LISTA_DO_TESTE = [...camadasDaGestaoQuery.queryKey, 'teste'] as const

/**
 * O modo de teste da URL, com o desenho e, no teste, o nome e a cor da camada.
 * Fora do modo de teste não pede nada à API: o observatório comum nunca toca
 * nas rotas da gestão.
 */
export function useCamadaEmTesteDaUrl(): CamadaEmTeste | undefined {
  const modo = useModoDeTeste()
  const caminho =
    modo === undefined
      ? null
      : modo.tipo === 'teste'
        ? `/v1/admin/camadas/${modo.camada}/desenho`
        : `/v1/admin/camadas/arquivos/${modo.arquivo}/desenho`

  const desenho = useQuery({
    queryKey: ['admin', 'desenho', caminho],
    enabled: caminho !== null,
    queryFn: async () =>
      lerDesenho(await apiFetch<unknown>(caminho ?? '', { headers: ACEITA_GEOJSON })),
  })
  const lista = useQuery({
    queryKey: CHAVE_DA_LISTA_DO_TESTE,
    enabled: modo?.tipo === 'teste',
    queryFn: async () => lerListaDaGestao(await apiFetch<unknown>('/v1/admin/camadas')),
  })

  if (modo === undefined) return undefined
  if (desenho.isError) return { estado: 'recusada', modo, recusa: recusaDe(desenho.error) }

  if (modo.tipo === 'previa') {
    if (desenho.data === undefined) return { estado: 'carregando', modo }
    return {
      estado: 'pronta',
      modo,
      nome: null,
      cor: modo.cor,
      desenho: desenho.data,
      acao: 'nenhuma',
      desativada: false,
    }
  }

  if (lista.isError) return { estado: 'recusada', modo, recusa: recusaDe(lista.error) }
  if (desenho.data === undefined || lista.data === undefined) return { estado: 'carregando', modo }
  const camada = lista.data.gestao.find((c) => c.id === modo.camada)
  if (camada === undefined) return { estado: 'recusada', modo, recusa: 'nao-encontrada' }
  return {
    estado: 'pronta',
    modo,
    nome: camada.nome,
    cor: camada.cor,
    desenho: desenho.data,
    acao: acaoDoTeste(lista.data.podeTestarEPublicar, camada.situacao),
    desativada: camada.situacao === 'desativada',
  }
}

/**
 * `POST /v1/admin/camadas/{id}/teste`: leva a camada a testada. Relê a lista da
 * aba e, por estar abaixo dela, a do teste.
 */
export function useConcluirTeste(camada: number) {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: () => apiFetch<unknown>(`/v1/admin/camadas/${camada}/teste`, { method: 'POST' }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: camadasDaGestaoQuery.queryKey }),
  })
}

// ── Ligada ou desligada na lista ─────────────────────────────────────────────

/*
 * A caixa da camada em teste, na lista do painel fixo e na da gaveta: as duas
 * mudam o mesmo desenho. Começa ligada — é para ver o desenho que o gestor
 * abriu o observatório. Desligar vale só para o modo em que foi feito: outra
 * camada (ou outra prévia) aberta na mesma aba começa ligada de novo.
 */
let desligado: string | null = null
const ouvintes = new Set<() => void>()

function chaveDoModo(modo: ModoDeTeste): string {
  return modo.tipo === 'teste' ? `teste:${modo.camada}` : `previa:${modo.arquivo}:${modo.cor}`
}

export function ligarCamadaEmTeste(modo: ModoDeTeste, valor: boolean): void {
  const proximo = valor ? null : chaveDoModo(modo)
  if (desligado === proximo) return
  desligado = proximo
  for (const ouvinte of ouvintes) ouvinte()
}

export function useCamadaEmTesteLigada(modo: ModoDeTeste | undefined): boolean {
  const atual = useSyncExternalStore(
    (aviso) => {
      ouvintes.add(aviso)
      return () => ouvintes.delete(aviso)
    },
    () => desligado
  )
  return modo !== undefined && atual !== chaveDoModo(modo)
}
